/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import classnames from 'classnames';
import { debounce, isEmpty } from 'lodash';

import Input from 'components/inputs/input/Input';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import Popover from 'components/popover/Popover';

import { __t, applyTranslations } from 'utils/language';
import { DEBOUNCE_DELAY } from 'modules/discovery/creators/utils/creators';

import './discovery-creators-filters-share.scss';

import type { DiscoveryCreatorsShareData, TextEntry } from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./discovery-creators-filters-share.i18n.json'));

type Props = {
  filterName: string,
  filter: DiscoveryCreatorsShareData,
  type: 'ages' | 'countries',
  isDisabled: boolean,
  onHandleUpdate: (a: DiscoveryCreatorsShareData) => void
};

type State = {
  shareValue: string
};

const msgKey = 'discovery_creators_filters_share';

class DiscoveryCreatorsFiltersShare extends React.PureComponent<Props, State> {
  debounceMethod;
  inputFieldRef: TextEntry | undefined | null = null;

  constructor(props: Props) {
    super(props);

    this.debounceMethod = debounce(props.onHandleUpdate, DEBOUNCE_DELAY);

    this.state = {
      shareValue: props.filter.value || ''
    };
  }

  componentDidUpdate(prevProps: Props) {
    const { filter } = this.props;

    // when resetting filters, use empty "share" value instead of local state
    if (filter.value !== prevProps.filter.value) {
      this.setState({
        shareValue: filter.value
      });
    }
  }

  getTypeValue(): string {
    const { type } = this.props;

    if (type === 'ages') {
      return 'age groups';
    }

    if (type === 'countries') {
      return 'countries';
    }

    return '';
  }

  getSafeValue(value: string): string {
    const safeValue = value.replace(/\D+/, '').slice(0, 3);
    const numberValue = Number(safeValue);
    const updatedValue = numberValue > 100 ? 100 : numberValue;

    return String(updatedValue);
  }

  handleInputRefCallback = (ref: TextEntry): void => {
    this.inputFieldRef = ref;
  };

  handleCheckboxToggle = (event: SyntheticEvent<HTMLInputElement>): void => {
    const { filter, onHandleUpdate } = this.props;
    const target = event.currentTarget;
    const isChecked = target.checked;
    const shareValue = isChecked && isEmpty(filter.value) ? '0' : filter.value;

    this.setState({
      shareValue
    }, () => {
      if (this.inputFieldRef) {
        // `!filter.checkbox.selected` is a condition for when the checkbox itself isn't clicked (e.g. auto-deselect)
        if (isChecked && !filter.checkbox.selected) {
          this.inputFieldRef.focus();
          // $FlowIgnore - Flow is too dumb to recognize that we are first checking if `this.inputFieldRef` exists
          this.inputFieldRef.select();
        } else {
          this.inputFieldRef.blur();
        }
      }
    });

    onHandleUpdate({
      checkbox: {
        ...filter.checkbox,
        selected: isChecked
      },
      value: shareValue
    });
  };

  handleValueChange = (event: SyntheticEvent<HTMLInputElement>): void => {
    const { filter } = this.props;
    const { shareValue } = this.state;
    const target = event.currentTarget;
    const value = this.getSafeValue(target.value);

    if (shareValue === value) {
      return;
    }

    this.setState({
      shareValue: value
    }, () => {
      if (this.debounceMethod) {
        this.debounceMethod({
          ...filter,
          value
        });
      }
    });
  };

  renderCheckbox(): React.ReactNode {
    const { filterName, filter, type, isDisabled } = this.props;

    return (
      <div className="discovery-creators-filters-share__checkbox">
        <InputCheckbox
          id={`discovery-creators-filters-share-checkbox--${type}-${filterName}`}
          name={`discovery-creators-filters-share-checkbox--${type}-${filterName}`}
          value={filter.checkbox.value}
          checked={filter.checkbox.selected}
          disabled={isDisabled}
          onChange={this.handleCheckboxToggle}
        />
      </div>
    );
  }

  renderInput(): React.ReactNode {
    const { filterName, filter, type, isDisabled } = this.props;
    const { shareValue } = this.state;
    const checkboxSelected = filter.checkbox.selected;

    const hintClasses = classnames('discovery-creators-filters-share__hint', {
      'discovery-creators-filters-share__hint--is-disabled': !checkboxSelected || isDisabled
    });

    return (
      <div className="discovery-creators-filters-share__field">
        <Input
          // TODO: Change Input.js `ref` implementation to the newer `createRef` style
          // $FlowIgnore - Flow doesn't like that `ref` in Input.js can be `null`
          refCallback={this.handleInputRefCallback}
          type="number"
          name={`discovery-creators-filters-share-value--${type}-${filterName}`}
          min={0}
          max={100}
          step={1}
          value={shareValue}
          hint="%"
          hintAlign="right"
          hintClassName={hintClasses}
          disabled={!checkboxSelected || isDisabled}
          className="discovery-creators-filters-share__input"
          autoComplete="off"
          onChange={this.handleValueChange}
        />
      </div>
    );
  }

  renderPopover(): React.ReactNode {
    return (
      <Popover
        questionMarkCustomSize={{
          width: 12,
          height: 12
        }}
        classNameWrapper="discovery-creators-filters-share__popover-wrapper"
        showQuestionMark
      >
        <div className="discovery-creators-filters-share__popover-contents">
          <div>
            {__t(`${msgKey}.tooltips.share_intro`)}
          </div>
          <div>
            {__t(`${msgKey}.tooltips.share_example`, {
              type: this.getTypeValue()
            })}
          </div>
        </div>
      </Popover>
    );
  }

  renderMessage(): React.ReactNode {
    return (
      <div className="discovery-creators-filters-share__message">
        <div>
          {__t(`${msgKey}.messages.share`, {
            type: this.getTypeValue()
          })}
        </div>
        <div className="discovery-creators-filters-share__popover">
          {this.renderPopover()}
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    return (
      <div className="discovery-creators-filters-share">
        {this.renderCheckbox()}
        {this.renderInput()}
        {this.renderMessage()}
      </div>
    );
  }
}

export default DiscoveryCreatorsFiltersShare;
